import { useEffect, useState } from 'react';
import { useSearchContext } from '../../../context/Search';
import useGoTo from '../../../common/hooks/useGoTo';
import slugify from '../../../../../utils/slugify';
import { getNestedValue } from '../../../common/utils/helpers';

const useOpenModalButtonLogic = ({ defaultText }) => {
  const {
    initialSearch,
    modal: [showModal, setShowModal],
    recentSearches: { lastSearch },
    searchAreaSuggest: { fetchSearchAreaSuggestions, topSearchAreaSuggestion },
    selectedKeyword: [selectedKeyword, setSelectedKeyword],
    selectedSearchArea: [selectedSearchArea, setSelectedSearchArea],
  } = useSearchContext();
  const goTo = useGoTo();
  const [hasValue, setHasValue] = useState(false);
  const [text, setText] = useState(defaultText);
  const [showButton, setShowButton] = useState(false);
  const [goToArgs, setGoToArgs] = useState({});

  const initialSearchKeyword = getNestedValue(initialSearch, 'keyword');
  const initialSearchAreaName = getNestedValue(initialSearch, 'searchAreaName');
  const lastSearchKeyword = getNestedValue(lastSearch, 'keyword');
  const lastSearchArea = getNestedValue(lastSearch, 'selectedSearchArea');
  const lastSearchAreaName = getNestedValue(lastSearchArea, 'name');

  const openModal = () => {
    setShowModal(true, { selectedKeyword, selectedSearchArea });
  };

  const handleButtonClick = event => {
    event.stopPropagation();
    goTo(goToArgs);
  };

  /**
   * Set the text based on the following priority:
   * - The category/search area page the user is on
   * - The user's most recent search
   * - Default text (also hide the Go button)
   */
  useEffect(() => {
    if (!showModal.show) {
      if (initialSearch) {
        if (initialSearchKeyword) {
          setSelectedKeyword(initialSearchKeyword);
          if (lastSearchAreaName) {
            setText(`${initialSearchKeyword} in ${lastSearchAreaName}`);
            setGoToArgs({
              keyword: initialSearchKeyword,
              path: `/find/${slugify(initialSearchKeyword)}/${
                lastSearchArea.path
              }`,
              selectedSearchArea: lastSearchArea,
            });
            setShowButton(true);
          } else {
            setShowButton(false);
            setText(initialSearchKeyword);
          }
        } else if (initialSearchAreaName === '') {
          setSelectedSearchArea(null);
          if (lastSearch) {
            setText(lastSearchKeyword);
          } else {
            setText(defaultText);
          }
        } else if (initialSearchAreaName) {
          if (lastSearchKeyword) {
            setText(`${lastSearchKeyword} in ${initialSearchAreaName}`);
          } else {
            setText(initialSearchAreaName);
          }
        } else {
          setShowButton(false);
          setText(defaultText);
        }
      } else if (lastSearch) {
        if (lastSearchKeyword) {
          setSelectedKeyword(lastSearchKeyword);
        }
        if (lastSearchArea) {
          setSelectedSearchArea(lastSearchArea);
        }
        setShowButton(true);
        setText(lastSearch.suggestLabel);
        setGoToArgs(lastSearch);
        setHasValue(true);
      } else {
        setShowButton(false);
        setText(defaultText);
        setGoToArgs({});
        setHasValue(false);
      }
    }
  }, [initialSearch, lastSearch, showModal]);

  useEffect(() => {
    if (initialSearch && initialSearchAreaName) {
      if (
        topSearchAreaSuggestion &&
        (!selectedSearchArea ||
          selectedSearchArea.name !== topSearchAreaSuggestion.name)
      ) {
        setSelectedSearchArea(topSearchAreaSuggestion);
      } else {
        fetchSearchAreaSuggestions({
          query: initialSearchAreaName,
          debounceFetch: false,
        });
      }
    }
  }, [topSearchAreaSuggestion, initialSearch]);
  return {
    handleButtonClick,
    handleClick: openModal,
    hasValue,
    showButton,
    text,
  };
};

export default useOpenModalButtonLogic;
